import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LevelMeter,
  listMicrophones,
  openMicrophone,
  playTestTone,
  readMicSettings,
  stopStream,
  type MicSettings,
} from '../lib/devices.ts';
import { useT } from '../i18n/index.tsx';

/**
 * Step 4 of the user story: pick a microphone, see that it hears you, and
 * confirm the speakers work — all before anyone is waiting on the other end.
 *
 * It also reads back which audio processing the browser actually applied,
 * because section 8 asks for the applied settings, not the requested ones.
 */
export function MicCheck({
  onReady,
  busy,
}: {
  onReady: (stream: MediaStream) => void;
  busy: boolean;
}) {
  const t = useT();
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');
  const [level, setLevel] = useState(0);
  const [settings, setSettings] = useState<MicSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tonePlayed, setTonePlayed] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const meterRef = useRef(new LevelMeter());

  const open = useCallback(
    async (id?: string) => {
      setError(null);
      try {
        const stream = await openMicrophone(id);
        // Swap, do not stack: a second live stream would keep the old device hot.
        stopStream(streamRef.current);
        streamRef.current = stream;

        const track = stream.getAudioTracks()[0];
        if (track) setSettings(readMicSettings(track));
        meterRef.current.start(stream, setLevel);
        setDevices(await listMicrophones());
        if (!id) setDeviceId(track?.getSettings().deviceId ?? '');
      } catch (err) {
        const name = err instanceof DOMException ? err.name : '';
        setError(
          name === 'NotAllowedError'
            ? t.check.micDenied
            : name === 'NotFoundError'
              ? t.check.micNotFound
              : t.check.micFailed,
        );
      }
    },
    [t],
  );

  useEffect(() => {
    void open();
    const meter = meterRef.current;
    return () => {
      meter.stop();
      // The stream is handed to the caller on success; on unmount without one,
      // releasing it is what turns the browser's recording light off.
      stopStream(streamRef.current);
      streamRef.current = null;
    };
  }, [open]);

  const applied = settings
    ? [
        settings.echoCancellation ? t.check.echoCancellation : null,
        settings.noiseSuppression ? t.check.noiseSuppression : null,
        settings.autoGainControl ? t.check.autoGain : null,
      ].filter(Boolean)
    : [];

  return (
    <div>
      {error && <div className="notice bad">{error}</div>}

      <div className="field">
        <label htmlFor="mic">{t.check.microphone}</label>
        <select
          id="mic"
          value={deviceId}
          onChange={(e) => {
            setDeviceId(e.target.value);
            void open(e.target.value);
          }}
        >
          {devices.length === 0 && <option value="">{t.check.defaultMic}</option>}
          {devices.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || t.check.microphone}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label>{t.check.level}</label>
        <div className="meter">
          <i style={{ width: `${Math.round(level * 100)}%` }} />
        </div>
      </div>

      {settings && (
        <p className="lede" style={{ fontSize: 13 }}>
          {t.check.applied(applied.length > 0 ? applied.join(', ') : t.check.appliedNone)}{' '}
          {t.check.measureNote}
        </p>
      )}

      <div className="actions">
        <button
          type="button"
          onClick={() => {
            void playTestTone().then(() => setTonePlayed(true));
          }}
        >
          {tonePlayed ? t.check.testAgain : t.check.testSound}
        </button>
        <button
          type="button"
          className="primary"
          disabled={busy || streamRef.current === null}
          onClick={() => {
            const stream = streamRef.current;
            // Ownership moves to the call; this component must not stop it now.
            streamRef.current = null;
            meterRef.current.stop();
            if (stream) onReady(stream);
          }}
        >
          {busy ? t.check.starting : t.check.start}
        </button>
      </div>
    </div>
  );
}
